import { PermissionSet, useDateRangeContext, usePermission, usePermissionsLoaded } from '@context'
import { autoUpdate, offset, shift, useDismiss, useFloating } from '@floating-ui/react'
import { formatDate } from '@utils'
import cn from 'classnames'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaCalendar, FaCaretDown, FaTimes } from 'react-icons/fa'
import { DateRangeDropdown } from './DateRangeDropdown'
import {
  StyledDateRangeSelect,
  StyledFilterLabel,
  StyledIconWrapper
} from './DateRangeSelect.styled'

export const DateRangeSelect: React.FC<{ compact: boolean }> = ({ compact }) => {
  const { t } = useTranslation()
  const { selectedRange, isActive, isDefaultRange, isReady, reset, refetch } = useDateRangeContext()
  const canFetchDateRanges = usePermission(PermissionSet.DATE_RANGE_VIEW)
  const permissionsLoaded = usePermissionsLoaded()
  const [dropdownOpen, setDropdownOpen] = useState(false)

  const { context, x, y, refs, strategy } = useFloating({
    open: dropdownOpen,
    onOpenChange: setDropdownOpen,
    placement: 'bottom-start',
    middleware: [offset(10), shift()],
    whileElementsMounted: autoUpdate
  })

  useDismiss(context)

  // Optimistic while permissions load — most users see the date filter.
  if (permissionsLoaded && !canFetchDateRanges) {
    return null
  }

  return (
    <>
      <StyledDateRangeSelect
        className={cn('date-range-select', { isActive, isCompact: compact })}
        ref={refs.setReference}
        onClick={() => {
          refetch()
          setDropdownOpen(!dropdownOpen)
        }}
        data-test="date-range-select"
      >
        <StyledIconWrapper className={cn({ isActive })}>
          <FaCalendar />
        </StyledIconWrapper>
        {!compact && (
          <>
            <StyledFilterLabel>
              <div>
                {selectedRange?.key ? t(`components.dateRangeFilter.${selectedRange?.key}`) : ''}
              </div>
              <span>
                {selectedRange?.date_from && selectedRange?.date_to
                  ? `${formatDate(selectedRange.date_from)} – ${formatDate(selectedRange.date_to)}`
                  : t('components.dateRangeFilter.noDateLimit')}
              </span>
            </StyledFilterLabel>{' '}
            {!isDefaultRange ? (
              <FaTimes
                className="reset-btn"
                onClick={e => {
                  reset()
                  e.stopPropagation()
                }}
                data-test="reset-date-range-button"
              />
            ) : (
              <FaCaretDown />
            )}
          </>
        )}
      </StyledDateRangeSelect>

      {isReady && (
        <DateRangeDropdown
          ref={refs.setFloating}
          isVisible={dropdownOpen}
          onClose={() => setDropdownOpen(false)}
          context={context}
          x={x}
          y={y}
          strategy={strategy}
        />
      )}
    </>
  )
}
